/* @flow */

import linkParser from 'rest/parsers/rfc5988';
import { createNetworkError } from './network_error';

var requestQueue = [];
const allowedQueueMethods = new Set(['GET', 'HEAD']);

function drainRequestQueue() {
  if (requestQueue.length) {
    // iterate over a stable list, requeueing if network flaps
    let queue = requestQueue;
    requestQueue = [];

    queue.forEach(func => func());
  }
}

function queueRequest(options) {
  return new Promise((resolve, reject) => {
    requestQueue.push(() => {
      xhr(options).then(resolve, reject);
    });
  });
}

function buildUri(options) {
  let uri = options.uri || '';
  let hasQueryString = uri.indexOf('?') !== -1;

  if (options.accessToken) {
    uri = `${uri}${hasQueryString ? '&' : '?'}access_token=${options.accessToken}`;
    hasQueryString = true;
  }
  if (options.cache === false) {
    uri = `${uri}${hasQueryString ? '&' : '?'}_=${Date.now()}`;
    hasQueryString = true;
  }

  return uri;
}

/**
 * Our promise-emitting XMLHttpRequest wrapper.
 *
 * @param {Object|String} options - If it's a string, it is interpreted as
 *   options.uri
 * @returns {Promise} a promise that resolves when a request is successful
 * and rejects when it is unsuccessful
 */
function xhrWithLinks(options: XHROptions | String): Promise<any> {
  if (typeof options === 'string') {
    options = { uri: options };
  }
  options = options || {};
  options.method = options.method || 'GET';

  if (options.fail) {
    // should only ever be used for testing
    return Promise.reject('Asked to fail');
  }

  if (!window.navigator.onLine) {
    if (!allowedQueueMethods.has(options.method.toUpperCase()) || options.offlineQueue === false) {
      // immediatly reject non GETs and HEADs
      return Promise.reject(createNetworkError());
    }
    // buffer request until online
    return queueRequest(options);
  }

  return new Promise((resolve, reject) => {
    var req = new XMLHttpRequest();
    if (options.onProgress) {
      req.onprogress = options.onProgress;
    }
    if (options.onUploadProgress) {
      req.upload.onprogress = options.onUploadProgress;
    }
    req.onerror = () => {
      reject(createNetworkError());
    };
    req.onload = () => {
      if (req.status >= 400) {
        var error;

        try {
          error = createNetworkError(JSON.parse(req.response).message);
        } catch (e) {
          error = typeof req.response === 'string' ?
            createNetworkError(req.response) :
            new Error(`${req.statusText}: ${options.uri}`);
        }

        error.status = req.status;
        reject(error);
      } else {
        try {
          var headers = parseHeaders(req.getAllResponseHeaders());
          var links = {};
          if (headers.link) {
            links = linkParser.parse(headers.link).reduce((links, link) => {
              links[link.rel] = link.href;
              return links;
            }, links);
          }
          if (options.parseJSON !== false) {
            var data = JSON.parse(req.response);
            resolve({
              entity: data,
              links: links
            });
          } else {
            resolve({
              entity: req.response
            });
          }
        } catch (e) {
          reject(new Error('invalid json'));
        }
      }
    };
    let uri = buildUri(options);
    req.open(options.method || 'GET', uri);
    if (options.withCredentials) req.withCredentials = true;
    if (options.destination) req.setRequestHeader('destination', options.destination);
    if (options.headers) {
      Object.keys(options.headers).forEach(header => {
        req.setRequestHeader(header, options.headers[header]);
      });
    }
    if (options.file) {
      req.setRequestHeader('Content-Type', 'application/octet-stream');
      req.send(options.file);
    } else if (options.json) {
      req.setRequestHeader('Content-Type', 'application/json');
      req.send(JSON.stringify(options.json));
    } else {
      req.send();
    }
  }).catch(err => {
    if (typeof options.retry === 'number' && options.retry > 0) {
      console.log(`Request for ${options.uri} failed, ${options.retry} attempts remaining:`, err);
      options.retry--;
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          xhrWithLinks(options).then(resolve, reject);
        }, 500);
      });
    }
    throw err;
  });
}

function xhr(options: XHROptions | String): Promise<any> {
  return xhrWithLinks(options).then(response => response.entity);
}

// lifted from rest/clients/xhr
function parseHeaders(raw) {
	// Note: Set-Cookie will be removed by the browser
	var headers = {};

	if (!raw) { return headers; }

	raw.trim().split(/[\r|\n]+/).forEach(function(header) {
		var boundary, name, value;
		boundary = header.indexOf(':');
		name = header.substring(0, boundary).trim().toLowerCase();
		value = header.substring(boundary + 1).trim();
		if (headers[name]) {
			if (Array.isArray(headers[name])) {
				// add to an existing array
				headers[name].push(value);
			} else {
				// convert single value to array
				headers[name] = [headers[name], value];
			}
		} else {
			// new, single value
			headers[name] = value;
		}
	});

	return headers;
}

export {
  xhr,
  xhrWithLinks,
  drainRequestQueue
};
